import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { DEFAULT_PDF_OPTIONS } from './pdf-definition';
import {
  get,
  merge,
  isEmpty,
  forEach,
  chunk,
  isString,
  isFunction,
  fill,
  concat,
  map,
  isArray,
} from 'lodash';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { HeaderConfig, PdfOptions } from './pdf-options.type';
import { PdfDataStructure } from './pdf-data-structure.type';
import { STATE_KEYS } from '@app/shared/services/state-service/state-selectors';
import { StateService } from '@shared/services/state-service';
import { lazyInject } from '@app/containers/base/decorators';
import { PdfWorker } from './pdf-worker';
import { PdfWorkerSelfType } from './pdf-worker.type';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
declare const self: PdfWorkerSelfType;
@Injectable()
export class PdfBaseService {
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(PdfWorker) public pdfWorker: PdfWorker;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public header: HeaderConfig;
  public content: object[] = [];
  public footer: object;
  public pdfParams: PdfOptions;
  public filename: string;
  public tableLineColor: string = '#BBBDC0';
  public greyBgColor: string = '#FAFAFA';
  public whiteBgColor: string = '#FFFFFF';
  public headerBgColor: string = '#F5F5F5';
  public fontColor: string = '#323232';
  public createDate: string;
  public serverDate: string = '';
  public specialPdfOptions: object;

  public constructor(public injector: Injector) {}

  public downloadPdf(customPdfOptions?: PdfOptions): void {
    this.generateFooter();
    const pdfDefinition = this.composePdfOptions(customPdfOptions);
    const globalOptions = this.generateGlobalOptions();
    this.pdfWorker.exportPdf(
      pdfDefinition as unknown as TDocumentDefinitions,
      globalOptions,
      this.filename,
    );
  }

  public printPdf(customPdfOptions?: PdfOptions): void {
    this.generateFooter();
    const pdfDefinition = this.composePdfOptions(customPdfOptions);
    const globalOptions = this.generateGlobalOptions();
    this.pdfWorker.printPdf(
      pdfDefinition as unknown as TDocumentDefinitions,
      globalOptions,
    );
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public generateHeader(header: HeaderConfig): void {
    this.header = header;
  }

  public generateFooter(): void {
    this.footer = (currentPage, pageCount): object[] => {
      const date = self.serverDate ? new Date(self.serverDate) : new Date();

      // self is worker global object, like window
      return [
        {
          style: 'footer',
          table: {
            widths: ['30%', '40%', '30%'],
            body: [
              [
                {
                  text: self.createDate,
                  alignment: 'left',
                  style: 'footerText',
                },
                {
                  text: `© ${date.getFullYear()} ${self.allRightsText}`,
                  alignment: 'center',
                  style: 'footerText',
                },
                {
                  text: `Page ${currentPage} of ${pageCount}`,
                  alignment: 'right',
                  style: 'footerText',
                },
              ],
              [
                {
                  colSpan: 3,
                  text: self.footerText,
                  alignment: 'center',
                  color: '#717073',
                  margin: [0, 3, 0, 0],
                },
              ],
            ],
          },
          layout: {
            vLineWidth: (): number => 0,
            hLineWidth: (i): number => (i === 1 ? 1 : 0),
          },
        },
      ];
    };
  }

  public generatePdfContent(pdfDataStructure: PdfDataStructure): void {
    this.cleanPdf();
    this.createDate = pdfDataStructure.createDate;
    // Push page header
    if (pdfDataStructure.header) {
      this.header = pdfDataStructure.header;
    }
    // Dynamically repeating headers
    if (
      pdfDataStructure.autoHeader &&
      isFunction(pdfDataStructure.autoHeader)
    ) {
      this.header = pdfDataStructure.autoHeader(pdfDataStructure);
    }
    // Special pdf options
    if (pdfDataStructure.specialPdfOptions) {
      this.specialPdfOptions = pdfDataStructure.specialPdfOptions;
    }
    // Push page title
    if (pdfDataStructure.pageTitle && !pdfDataStructure.autoHeader) {
      this.content.push({ style: 'title', text: pdfDataStructure.pageTitle });
    }
    // Generate summary
    if (!isEmpty(pdfDataStructure.summary)) {
      const columnsPerRow = get(pdfDataStructure, 'summary.columnsPerRow', 3);
      const groupByColumnsPerRow = chunk(
        get(pdfDataStructure, 'summary.data', []),
        columnsPerRow,
      );
      const defaultColumnWidth = `${(100 / columnsPerRow).toFixed(2)}%`;
      forEach(groupByColumnsPerRow, (groupedData) => {
        const summary = {
          style: 'summary',
          margin: [0, 0, 0, 5],
          columns: [],
        };
        forEach(groupedData, (item) => {
          const column = {
            text: [
              { text: get(item, 'fieldName', '') },
              { text: get(item, 'value', ''), bold: true },
            ],
            width: get(item, 'width', defaultColumnWidth),
          };
          summary.columns.push(column);
        });
        if (columnsPerRow - groupedData.length !== 0) {
          for (let i = 0; i < columnsPerRow - groupedData.length; i += 1) {
            summary.columns.push({});
          }
        }
        this.content.push(summary);
      });
      // Add blank columns for padding bottom
      this.content.push({ margin: [0, 0, 0, 5], columns: [] });
    }
    // Generate table
    const mainContent = [];
    forEach(pdfDataStructure.tableData, (eachTable) => {
      const headers = get(eachTable, 'headers', []);
      const columnDistributionType: string = pdfDataStructure.equalColumnWidth
        ? '*'
        : 'auto';
      let tableWidth: (string | number)[] = fill(
        Array(headers.length),
        columnDistributionType,
      );
      // The first column displays the name (such as inventory item name),
      // and the content may be relatively long, so configure the width of the first column separately.
      if (pdfDataStructure.useSmallerFontInTable) {
        tableWidth[1] = 40;
      } else {
        tableWidth[0] = '20%';
        tableWidth[1] = '*';
      }
      // Custom width
      tableWidth = map(tableWidth, (value, index) => {
        return headers[index]?.width || value;
      });

      const tableBodyData = get(eachTable, 'bodyData', []);
      forEach(tableBodyData, (tableItem) => {
        const tableTitle = {
          text: tableItem.tableTitle,
          fontSize: 11,
          bold: true,
          margin: [0, 0, 0, 5],
        };
        mainContent.push(tableTitle);

        forEach(
          isArray(tableItem.subTitle)
            ? tableItem.subTitle
            : [tableItem.subTitle],
          (subTitleItem: string) => {
            const subTitle = {
              text: subTitleItem,
              fontSize: 8,
              margin: [0, 0, 0, 5],
            };
            mainContent.push(subTitle);
          },
        );

        const tableStructure = {
          style: 'table',
          table: {
            headerRows: 1,
            widths: tableWidth,
            body: [],
          },
          // This property is missing from pdfmake config, It have two default value 'after' & 'before'
          pageBreak: tableItem.pageBreakValue,
          layout: {
            defaultBorder: true,
            hLineColor: this.tableLineColor,
            vLineColor: this.tableLineColor,
            hLineWidth: (): number => 1,
            vLineWidth: (): number => 1,
          },
        };
        const headerRow = [];
        forEach(headers, (item) => {
          headerRow.push({
            text: isString(item) ? item : item.text,
            alignment: item?.textAlign || 'left',
            bold: !pdfDataStructure.useSmallerFontInTable,
            color: this.fontColor,
            fontSize: pdfDataStructure.useSmallerFontInTable ? 7 : 10,
            fillColor: this.headerBgColor,
          });
        });
        tableStructure.table.body.push(headerRow);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        forEach(tableItem.data, (bodyItem, rowIndex) => {
          const bodyRow = [];
          forEach(bodyItem, (cellObj) => {
            bodyRow.push({
              ...(cellObj.customContent ?? {
                text: isString(cellObj) ? cellObj : cellObj.text,
                rowSpan: cellObj?.rowSpan || 1,
              }),

              bold: false,
              color: cellObj.color || this.fontColor,
              alignment: cellObj?.textAlign || 'left',
              fontSize: pdfDataStructure.useSmallerFontInTable ? 5 : 9,
              fillColor: this.whiteBgColor,
            });
          });
          tableStructure.table.body.push(bodyRow);
        });
        mainContent.push(tableStructure);

        const postBodyData = get(tableItem, 'postBodyData', []);
        if (!isEmpty(postBodyData)) {
          mainContent.push(postBodyData);
        }
      });
    });

    this.content.push(mainContent);
    // Generate total table
    if (!isEmpty(get(pdfDataStructure, 'totalTableData'))) {
      const totalTable = [];
      totalTable.push({
        text: get(pdfDataStructure, 'totalTableData.tableTitle', ''),
        fontSize: 11,
        bold: true,
        margin: [0, 10, 0, 5],
      });
      const totalHeaders = get(pdfDataStructure, 'totalTableData.headers', []);
      const totalTableWidths = fill(Array(totalHeaders.length), '*');
      const totalTableStructure = {
        style: 'table',
        table: {
          headerRows: 1,
          widths: totalTableWidths,
          body: [],
        },
        layout: {
          defaultBorder: true,
          hLineColor: this.tableLineColor,
          vLineColor: this.tableLineColor,
          hLineWidth: (): number => 1,
          vLineWidth: (): number => 1,
          fillColor: (rowIndex): string =>
            rowIndex % 2 === 0 ? this.greyBgColor : this.whiteBgColor,
        },
      };
      const totalHeaderRow = [];
      forEach(get(pdfDataStructure, 'totalTableData.headers', []), (header) => {
        totalHeaderRow.push({
          text: header.text,
          bold: true,
          color: this.fontColor,
          alignment: header.textAlign,
          fontSize: 10,
          fillColor: this.headerBgColor,
        });
      });
      totalTableStructure.table.body.push(totalHeaderRow);
      forEach(
        get(pdfDataStructure, 'totalTableData.bodyData', []),
        (rowData, index) => {
          const bodyRow = [];
          forEach(rowData, (cell) => {
            bodyRow.push({
              text: cell.text,
              bold:
                get(pdfDataStructure, 'totalTableData.bodyData.length', 0) ===
                index + 1,
              color: this.fontColor,
              fontSize: 9,
              alignment: cell.textAlign,
            });
          });
          totalTableStructure.table.body.push(bodyRow);
        },
      );
      totalTable.push(totalTableStructure);
      this.content.push(totalTable);
    }
    // Generate summary table
    if (!isEmpty(get(pdfDataStructure, 'summaryTableData'))) {
      const bottomSummaryTable = [];
      bottomSummaryTable.push({
        text: get(pdfDataStructure, 'summaryTableData.tableTitle', ''),
        fontSize: 11,
        bold: true,
        margin: [0, 10, 0, 5],
      });
      const bottomSummaryHeaders = get(
        pdfDataStructure,
        'summaryTableData.headers',
        [],
      );
      const bottomSummaryTableWidths = fill(
        Array(bottomSummaryHeaders.length),
        '*',
      );
      const bottomSummaryTableStructure = {
        style: 'table',
        table: {
          headerRows: 1,
          widths: bottomSummaryTableWidths,
          body: [],
        },
        layout: {
          defaultBorder: true,
          hLineColor: this.tableLineColor,
          vLineColor: this.tableLineColor,
          hLineWidth: (): number => 1,
          vLineWidth: (): number => 1,
          fillColor: (rowIndex): string =>
            rowIndex % 2 === 0 ? this.greyBgColor : this.whiteBgColor,
        },
      };
      const totalHeaderRow = [];
      forEach(
        get(pdfDataStructure, 'summaryTableData.headers', []),
        (header) => {
          totalHeaderRow.push({
            text: header.text,
            bold: true,
            color: this.fontColor,
            alignment: header.textAlign,
            fontSize: 10,
            fillColor: this.headerBgColor,
          });
        },
      );
      bottomSummaryTableStructure.table.body.push(totalHeaderRow);
      forEach(
        get(pdfDataStructure, 'summaryTableData.bodyData', []),
        (rowData, index) => {
          const bodyRow = [];
          forEach(rowData, (cell) => {
            bodyRow.push({
              text: cell.text,
              bold:
                get(pdfDataStructure, 'summaryTableData.bodyData.length', 0) ===
                index + 1,
              color: this.fontColor,
              fontSize: 9,
              alignment: cell.textAlign,
            });
          });
          bottomSummaryTableStructure.table.body.push(bodyRow);
        },
      );
      bottomSummaryTable.push(bottomSummaryTableStructure);
      this.content.push(bottomSummaryTable);
    }
    const signatureData = get(pdfDataStructure, 'signatureData', []);
    // Generate signatureData
    this.content = concat(this.content, signatureData);
    // Handle pdf download name
    if (pdfDataStructure.filename) {
      this.filename = pdfDataStructure.filename;
    } else if (
      pdfDataStructure.pageTitle &&
      pdfDataStructure.id &&
      !pdfDataStructure.bulkExport
    ) {
      this.filename = `${pdfDataStructure.pageTitle} - ${pdfDataStructure.id}`;
    }
  }

  public cleanPdf(): void {
    this.content = [];
    this.filename = '';
    this.header = null;
    this.specialPdfOptions = null;
  }

  protected composePdfOptions(
    customPdfOptions: PdfOptions,
  ): Partial<PdfOptions> {
    return merge(
      {},
      DEFAULT_PDF_OPTIONS,
      {
        header: this.header,
        content: this.content,
        footer: this.footer,
        pageBreakBefore: (
          currentNode,
          followingNodesOnPage,
          nodesOnNextPage,
          previousNodesOnPage,
        ): boolean => {
          const firstNodeOnNextPage: Record<string, string> =
            nodesOnNextPage[0];
          if (previousNodesOnPage.length <= 0 || nodesOnNextPage.length <= 0) {
            return false;
          }

          // Venues Inventory Uniform conventions: when the node has the style signature it is a signature node
          return (
            firstNodeOnNextPage?.style === 'signature' &&
            currentNode?.style === 'signature'
          );
        },
      },
      customPdfOptions,
      this.specialPdfOptions,
    );
  }

  /**
   * Generate global options
   * Need set it in onmessageHandle of pdf-orker.ts
   *
   * @returns object
   * @example
   * self.createDate = globalOptions.createDate;
   */
  protected generateGlobalOptions(): PdfWorkerSelfType {
    const serverDate = this.stateService.getByPath(
      STATE_KEYS.API_INFO,
    )?.serverTimestamp;

    return {
      serverDate,
      createDate: this.createDate,
      allRightsText: this.i18nService.instant(I18n.InfoMessages.AllRightsClaim),
      footerText: this.i18nService.instant(I18n.InfoMessages.ExportFooterText),
      i18n: {
        Page: this.i18nService.instant(I18n.Common.Page),
        of: this.i18nService.instant(I18n.Common.of),
      },
    };
  }
}
